import React, { useState } from "react";
import clsx from "clsx";
import { Camera, Store } from "lucide-react";
import { CATEGORY_COLORS, catIllustration, fmtNum, fmtUnit } from "../lib/constants";
import { compressImage } from "../lib/image";
import { useToast } from "../lib/toast";

/* ---------------- ProductAvatar: image with category-illustration fallback ---------------- */
export function ProductAvatar({ image, colorKey, name, size = "md", className }) {
  const [err, setErr] = useState(false);
  const sizes = {
    sm: "h-10 w-10 rounded-lg",
    md: "h-14 w-14 rounded-xl",
    lg: "h-20 w-20 rounded-2xl",
    xl: "w-full aspect-square rounded-2xl",
  };
  const cc = CATEGORY_COLORS[colorKey] || CATEGORY_COLORS.packaging;
  const src = !err && image ? image : catIllustration(colorKey);
  return (
    <div
      className={clsx("flex shrink-0 items-center justify-center overflow-hidden border", sizes[size], className)}
      style={{ background: cc.bg, borderColor: cc.border }}
    >
      <img src={src} alt={name || ""} onError={() => setErr(true)} className="h-full w-full object-cover" loading="lazy" />
    </div>
  );
}

/* ---------------- QtyUnit: never show a quantity without a unit ---------------- */
export function QtyUnit({ qty, unit, className, accent = false }) {
  return (
    <span className={clsx("inline-flex items-baseline gap-1", className)}>
      <span className={clsx("font-bold", accent ? "text-primary" : "text-ink")}>{fmtNum(qty)}</span>
      <span className="text-xs font-bold uppercase tracking-wider text-ink-mute">{fmtUnit(unit)}</span>
    </span>
  );
}

/* ---------------- StockHealthBar: visual current vs min/ideal ---------------- */
export function StockHealthBar({ current, minimum, ideal, status, showPct = true }) {
  const cur = Number(current || 0);
  const target = Number(ideal || 0) || Number(minimum || 0) * 2 || cur || 1;
  const pct = Math.max(2, Math.min(100, Math.round((cur / target) * 100)));
  const tone = status === "CRITICAL" ? "bg-danger" : status === "LOW" ? "bg-warn" : "bg-success";
  return (
    <div className="flex items-center gap-2">
      <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-stone-100">
        <div className={clsx("h-full rounded-full transition-all duration-500", tone)} style={{ width: `${pct}%` }} />
        {minimum > 0 && target > 0 && (
          <div
            className="absolute top-0 h-full w-0.5 bg-ink/30"
            style={{ left: `${Math.min(100, (Number(minimum) / target) * 100)}%` }}
            title="Minimum"
          />
        )}
      </div>
      {showPct && <span className="w-9 shrink-0 text-right text-xs font-semibold text-ink-soft">{pct}%</span>}
    </div>
  );
}

/* ---------------- BranchBadge: logo + name identity ---------------- */
export function BranchBadge({ branch, size = "md", showCode = false, className }) {
  const [err, setErr] = useState(false);
  if (!branch) return null;
  const sizes = { sm: "h-7 w-7 text-xs", md: "h-10 w-10 text-sm", lg: "h-14 w-14 text-lg" };
  const logo = !err && branch.logo;
  return (
    <div className={clsx("flex items-center gap-2.5", className)}>
      <div className={clsx("flex items-center justify-center overflow-hidden rounded-full bg-primary-light font-bold text-primary ring-2 ring-white", sizes[size])}>
        {logo ? <img src={branch.logo} alt={branch.name} onError={() => setErr(true)} className="h-full w-full object-cover" /> : (branch.name?.[0] || <Store size={16} />)}
      </div>
      <div className="leading-tight">
        <p className="font-heading text-sm font-bold text-ink">{branch.name}</p>
        {showCode && branch.code && <p className="text-[11px] font-medium text-ink-mute">{branch.code}</p>}
      </div>
    </div>
  );
}

/* ---------------- ImageUpload: compresses client-side, returns base64 ---------------- */
export function ImageUpload({ value, onChange, label = "Image", shape = "square", testid }) {
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const handle = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Image is too large (max 8MB).");
      e.target.value = "";
      return;
    }
    setBusy(true);
    try {
      const b64 = await compressImage(f, shape === "round" ? 320 : 640, 0.82);
      onChange(b64);
    } catch {
      toast.error("Could not process that image. Please try another.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  const round = shape === "round";
  return (
    <div>
      {label && <label className="cf-label">{label}</label>}
      <div className="flex items-center gap-3">
        <div className={clsx("flex items-center justify-center overflow-hidden border border-line bg-stone-50", round ? "h-16 w-16 rounded-full" : "h-16 w-16 rounded-xl")}>
          {value ? <img src={value} alt="" className="h-full w-full object-cover" /> : <Camera size={20} className="text-ink-mute" />}
        </div>
        <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-line px-3 py-2.5 text-sm font-medium text-ink-soft hover:border-primary hover:text-primary">
          <Camera size={16} /> {busy ? "Processing..." : value ? "Change" : "Upload"}
          <input type="file" accept="image/*" onChange={handle} className="hidden" data-testid={testid} />
        </label>
        {value && (
          <button type="button" onClick={() => onChange(null)} className="text-xs font-semibold text-danger hover:underline">Remove</button>
        )}
      </div>
    </div>
  );
}
